// BUILD-042 (Oct 24 guided intake and HR workspace) - ONE shared frame for every HR workspace page that has a secondary rail
// (HR Administration, an employee's admin page, My HR).
//
// Layout contract (Owner decision 2026-10-24, "Overall HR Administration composition"):
//   * the compact secondary rail (~216px) sits immediately beside the global HQ navigation, top-aligned with the page content;
//   * the content column is left-aligned next to it with a readable maximum width (~1100px) - the two-column workspace is NEVER centred as a whole,
//     so the rail cannot float mid-page on a wide screen;
//   * below ~900px the rail collapses into a "Menu" disclosure button (aria-expanded / aria-controls, a real <button>, so Enter and Space work and
//     Escape closes it). There are never two competing fixed columns on a small screen.
// Presentation only: it adds no route, permission or data. Pages pass their rail content and the label of the current destination.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export function HrWorkspaceFrame({
  rail, railLabel, currentLabel, children, testId = 'hr-workspace-frame', wide = false, stickyRail = false,
}: {
  /** Cycle 12: keep the rail anchored in view while a long page scrolls (wide screens only; the narrow Menu control is unchanged). */
  stickyRail?: boolean;
  /** A screen that genuinely needs more than the readable width (the Form Library's document editor). The rail and gutters are unchanged. */
  wide?: boolean;
  /** The secondary rail content (a nav, or an identity card plus a nav). */
  rail: ReactNode;
  /** Accessible name of the disclosure panel, e.g. "HR Administration menu". */
  railLabel: string;
  /** The current destination, shown on the narrow-screen "Menu" button so you can see where you are with the menu closed. */
  currentLabel?: string;
  children: ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const location = useLocation();

  // Choosing a destination (a route change) closes the narrow-screen menu.
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  return (
    <div
      className="flex flex-col min-[900px]:flex-row min-[900px]:items-start gap-3 min-[900px]:gap-6 min-w-0"
      data-testid={testId}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) { setOpen(false); buttonRef.current?.focus(); }
      }}
    >
      <div className={`min-w-0 min-[900px]:w-[216px] min-[900px]:flex-shrink-0 ${stickyRail ? 'min-[900px]:sticky min-[900px]:top-4 min-[900px]:max-h-[calc(100vh-2rem)] min-[900px]:overflow-y-auto' : ''}`} data-testid="hr-rail-column">
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          data-testid="hr-rail-toggle"
          className="min-[900px]:hidden w-full flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
        >
          <span className="min-w-0 truncate"><span className="font-semibold text-gray-900">Menu</span>{currentLabel ? <span className="text-gray-500"> · {currentLabel}</span> : null}</span>
          <svg className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        <div
          id={panelId}
          role="region"
          aria-label={railLabel}
          data-testid="hr-rail-panel"
          className={`${open ? 'block' : 'hidden'} min-[900px]:block mt-2 min-[900px]:mt-0 space-y-3 min-w-0`}
          onClick={(e) => {
            // A destination button inside the rail (in-page section switch) also closes the narrow menu; a disclosure inside the rail ("More") does not.
            const el = (e.target as HTMLElement).closest('a,button');
            if (el && !el.closest('[data-rail-keep-open]')) setOpen(false);
          }}
        >
          {rail}
        </div>
      </div>
      <div className={`flex-1 min-w-0 w-full ${wide ? '' : 'max-w-[1100px]'}`} data-testid="hr-content-column">{children}</div>
    </div>
  );
}

/** The page container for HR pages WITHOUT a rail (Directory, My Team, messages): same gutters and readable width, left-aligned (never centred). */
export function HrContentColumn({ children, testId }: { children: ReactNode; testId?: string }) {
  return <div className="min-w-0 max-w-[1100px]" data-testid={testId}>{children}</div>;
}
